import React from 'react';
import { Button } from 'baseui/button';
import { StatefulPopover, PLACEMENT } from 'baseui/popover';
import { useStyletron } from 'baseui';
import { CustomTheme } from '../baseweb-custom-theme/customTheme';
import { Block } from 'baseui/block';
import { Check, ChevronRight } from 'baseui/icon';
import {
  SortingDateCreatedProps,
  SortingLastModifiedProps,
  SortingNameProps,
} from '../relay-messenger-list/RelayMessenger.list';
export enum NameAscendingDescendingMethods {
  Ascending = 'Ascending',
  Descending = 'Descending',
  Name = 'Name',
}

interface SortingActionItemProps {
  color?: string;
  dataTestId?: string;
  icon?: () => React.ReactNode;
  sortingName: SortingNameProps;
  sortDateCreated: SortingDateCreatedProps;
  sortLastModified: SortingLastModifiedProps;
  close: () => void;
  clearFilterState: () => void;
  clearSortingSelection: () => void;
}

export const SortNameAction = ({
  dataTestId,
  sortingName,
  sortDateCreated,
  sortLastModified,
  close,
  clearFilterState,
  clearSortingSelection,
}: SortingActionItemProps) => {
  const [, theme] = useStyletron();
  const customTheme = theme as CustomTheme;

  return (
    <StatefulPopover
      content={() => (
        <Block
          overrides={{
            Block: {
              style: {
                width: '150px',
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
              },
            },
          }}
        >
          <Button
            onClick={(e) => {
              sortingName.setSortByAsc((e.target as HTMLButtonElement).value);
              sortingName.setSortByDesc(null);
              sortDateCreated.setSortByOldestDateCreated(null);
              sortDateCreated.setSortByNewestDateCreated(null);
              sortLastModified.setSortByNewestLastModified(null);
              sortLastModified.setSortByOldestLastModified(null);
              close();
            }}
            value={NameAscendingDescendingMethods.Ascending}
            overrides={{
              BaseButton: {
                style: {
                  padding: '10px',
                  width: '100%',
                  display: 'flex',
                  color:
                    sortingName.sortingOptionNameClicked &&
                    sortingName.sortByAsc
                      ? '#3693BF'
                      : 'black',
                  fontWeight:
                    sortingName.sortingOptionNameClicked &&
                    sortingName.sortByAsc
                      ? 'bolder'
                      : 'inherit',
                  backgroundColor: 'transparent',
                  textAlign: 'center',
                  justifyContent: 'left',
                  fontSize: '12px',
                  outline: 'none !important',
                  ':hover': {
                    backgroundColor: '#E1EFF5',
                  },
                  ':active': {
                    backgroundColor: 'transparent',
                  },
                },
                props: {
                  'data-testid': `${dataTestId}-ascending`,
                },
              },
            }}
            startEnhancer={() =>
              sortingName.sortingOptionNameClicked && sortingName.sortByAsc ? (
                <Check
                  size={'20px'}
                  color="#3693BF"
                  overrides={{
                    Svg: {
                      style: {
                        display: 'flex',
                      },
                    },
                  }}
                />
              ) : (
                ''
              )
            }
          >
            {NameAscendingDescendingMethods.Ascending}
          </Button>
          <Button
            onClick={(e) => {
              sortingName.setSortByDesc((e.target as HTMLButtonElement).value);
              sortingName.setSortByAsc(null);
              sortDateCreated.setSortByOldestDateCreated(null);
              sortDateCreated.setSortByNewestDateCreated(null);
              sortLastModified.setSortByNewestLastModified(null);
              sortLastModified.setSortByOldestLastModified(null);
              close();
            }}
            value={NameAscendingDescendingMethods.Descending}
            overrides={{
              BaseButton: {
                style: {
                  padding: '10px',
                  width: '100%',
                  display: 'flex',
                  color:
                    sortingName.sortingOptionNameClicked &&
                    sortingName.sortByDesc
                      ? '#3693BF'
                      : 'black',
                  fontWeight:
                    sortingName.sortingOptionNameClicked &&
                    sortingName.sortByDesc
                      ? 'bolder'
                      : 'inherit',
                  backgroundColor: 'transparent',
                  textAlign: 'center',
                  justifyContent: 'left',
                  fontSize: '12px',
                  outline: 'none !important',
                  ':hover': {
                    backgroundColor: '#E1EFF5',
                  },
                  ':active': {
                    backgroundColor: 'transparent',
                  },
                },
                props: {
                  'data-testid': `${dataTestId}-descending`,
                },
              },
            }}
            startEnhancer={() =>
              sortingName.sortingOptionNameClicked && sortingName.sortByDesc ? (
                <Check
                  size={'20px'}
                  color="#3693BF"
                  overrides={{
                    Svg: {
                      style: {
                        display: 'flex',
                      },
                    },
                  }}
                />
              ) : (
                ''
              )
            }
          >
            {NameAscendingDescendingMethods.Descending}
          </Button>
        </Block>
      )}
      placement={PLACEMENT.right}
      autoFocus={false}
      overrides={{
        Inner: {
          style: {
            backgroundColor: customTheme.colors.white,
            borderBottomLeftRadius: '5px',
            borderBottomRightRadius: '5px',
            borderTopLeftRadius: '5px',
            borderTopRightRadius: '5px',
          },
          props: { 'data-testid': 'sorting-name-popover' },
        },
        Body: {
          style: {
            borderBottomLeftRadius: '5px',
            borderBottomRightRadius: '5px',
            borderTopLeftRadius: '5px',
            borderTopRightRadius: '5px',
            marginTop: '0',
            marginLeft: '25px',
          },
        },
      }}
    >
      <Block
        overrides={{
          Block: {
            style: {
              width: '190px',
            },
          },
        }}
      >
        <Button
          onClick={() => {
            sortingName.setSortOptionNameClicked(
              NameAscendingDescendingMethods.Name,
            );
            sortDateCreated.setSortOptionDateCreatedClicked(null);
            sortLastModified.setSortOptionLastModifiedClicked(null);
            clearFilterState();
            clearSortingSelection();
          }}
          value={NameAscendingDescendingMethods.Name}
          overrides={{
            BaseButton: {
              style: {
                width: '100%',
                display: 'flex',
                color: sortingName.sortingOptionNameClicked
                  ? '#3693BF'
                  : 'black',
                fontWeight: sortingName.sortingOptionNameClicked
                  ? 'bolder'
                  : 'inherit',
                backgroundColor: sortingName.sortingOptionNameClicked
                  ? '#E1EFF5'
                  : 'transparent',
                textAlign: 'left',
                fontSize: '12px',
                outline: 'none !important',
                justifyContent: 'left',
                ':hover': {
                  backgroundColor: 'none',
                },
                ':active': {
                  backgroundColor: 'none',
                },
              },
              props: {
                'data-testid': 'name-btn',
              },
            },
          }}
          endEnhancer={() => <ChevronRight />}
          startEnhancer={() =>
            sortingName.sortingOptionNameClicked &&
            (sortingName.sortByAsc || sortingName.sortByDesc) ? (
              <Check size={'30px'} color="#3693BF" />
            ) : (
              ''
            )
          }
        >
          {NameAscendingDescendingMethods.Name}
        </Button>
      </Block>
    </StatefulPopover>
  );
};
